import React, {useEffect} from 'react';
import {Box, TextField, Typography} from '@mui/material';

interface MacAddressInputProps {
  value: string;
  onChange: (value: string) => void;
  /** When set (e.g. "VI"), the first segment is locked and always uses this prefix. */
  fixedPrefix?: string;
}

const MAC_CHAR_REGEX = /[^0-9A-Fa-fVI]/g;
const MAC_CHAR_UPPER_REGEX = /[^0-9A-FVI]/g;

function cleanMacChars(text: string): string {
  return text.toUpperCase().replace(MAC_CHAR_UPPER_REGEX, '');
}

function toParts(cleaned: string, fixedPrefix?: string): string[] {
  const parts = cleaned.match(/.{1,2}/g) || Array(6).fill('');
  while (parts.length < 6) {
    parts.push('');
  }
  if (fixedPrefix) {
    parts[0] = fixedPrefix.toUpperCase();
  }
  return parts.slice(0, 6);
}

const MacAddressInput: React.FC<MacAddressInputProps> = ({
  value = '',
  onChange,
  fixedPrefix,
}) => {
  const prefix = fixedPrefix?.toUpperCase();
  const cleanValue = value.replace(MAC_CHAR_REGEX, '');
  const parts = toParts(cleanValue, prefix);

  // Keep the locked prefix present in the form value (e.g. new device starts as "VI")
  useEffect(() => {
    if (!prefix) return;
    const cleaned = value.replace(MAC_CHAR_REGEX, '').toUpperCase();
    if (!cleaned.startsWith(prefix)) {
      onChange(prefix + cleaned.slice(0, 10));
    }
  }, [prefix, value, onChange]);

  const emitFromParts = (nextParts: string[]) => {
    if (prefix) {
      nextParts[0] = prefix;
    }
    onChange(nextParts.join(''));
  };

  const handleChange = (index: number, newValue: string) => {
    if (prefix && index === 0) return;

    const hexValue = cleanMacChars(newValue).slice(0, 2);
    const nextParts = [...parts];
    nextParts[index] = hexValue;
    emitFromParts(nextParts);
  };

  const handlePaste = (index: number, e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    let cleanedPaste = cleanMacChars(e.clipboardData.getData('text'));

    if (prefix) {
      // MAC that includes the fixed prefix (e.g. VI38B1162ACE or VI:38:B1:16:2A:CE)
      if (cleanedPaste.startsWith(prefix)) {
        const remainder = cleanedPaste.slice(prefix.length).slice(0, 10);
        emitFromParts(toParts(prefix + remainder, prefix));
        return;
      }

      // Paste without prefix but enough for remaining segments (e.g. 38B1162ACE)
      if (cleanedPaste.length >= 10) {
        emitFromParts(toParts(prefix + cleanedPaste.slice(0, 10), prefix));
        return;
      }

      // Shorter paste into a non-prefix field
      if (index === 0) return;
      const hexValue = cleanedPaste.slice(0, 2);
      const nextParts = [...parts];
      nextParts[index] = hexValue;
      emitFromParts(nextParts);
      return;
    }

    // No fixed prefix: original full-MAC paste behavior
    if (cleanedPaste.length >= 12) {
      onChange(cleanedPaste.slice(0, 12));
    } else {
      const hexValue = cleanedPaste.slice(0, 2);
      const nextParts = [...parts];
      nextParts[index] = hexValue;
      onChange(nextParts.join(''));
    }
  };

  return (
    <Box sx={{mb: 2}}>
      <Typography variant="body1" sx={{mb: 1, fontWeight: 400, color: 'text.secondary'}}>
        MAC Address <span style={{color: 'inherit'}}>*</span>
      </Typography>
      <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
        {Array.from({length: 6}).map((_, index) => (
          <React.Fragment key={index}>
            <TextField
              value={parts[index] || ''}
              onChange={e => handleChange(index, e.target.value)}
              onPaste={e => handlePaste(index, e)}
              inputProps={{
                maxLength: 2,
                style: {textAlign: 'center'},
                readOnly: Boolean(prefix && index === 0),
              }}
              size="small"
              sx={{
                width: 52,
                ...(prefix && index === 0
                  ? {
                      '& .MuiInputBase-input': {
                        bgcolor: 'action.hover',
                        cursor: 'default',
                      },
                    }
                  : {}),
              }}
              variant="outlined"
            />
            {index < 5 && <Typography variant="h6">:</Typography>}
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
};

export default MacAddressInput;
